<template>
  <div v-if="isOpen">
    <div class="modal-overlay" @click="$emit('close-modal')"></div>

    <div class="modal-content">
      <div class="absolute w-full z-10 xl:block hidden">
        <div class="bg-[#30ADBA] h-[300px] rounded-t-[20px]"></div>
        <div class="absolute top-[50%]">
          <figure class="w-[551px]">
            <img
loading="lazy"
              src="/img/partners-form/illustration.png"
              class="object-contain"
            />
          </figure>
        </div>
      </div>
      <div
        ref="form_elem"
        class="modal-content__container h-full relative w-full"
      >
        <div
          class="ml-auto text-right cursor-pointer relative z-20 pt-4 mb-[22px] xl:mr-0 mr-4"
          @click="$emit('close-modal')"
        >
          <icon-close
            class="xl:text-white text-[#1F1F1F] w-[32px] h-[32px] ml-auto"
          />
        </div>
        <div
          class="xl:ml-auto xl:w-[55%] w-full xl:rounded-[16px] xl:px-8 xl:pr-8 px-6 md:pr-0 pr-6 md:pb-[42px] pb-8 xl:pt-8 bg-white relative z-20"
        >
          <div class="xl:text-center mb-6 flex md:flex-row flex-col">
            <figure class="xl:hidden block w-[248px] shrink-0 mx-auto md:mx-0">
              <img
loading="lazy"
                src="/img/partners-form/illustration.png"
                class="object-contain"
              />
            </figure>
            <PartnersFormHeader :type="type" />
          </div>
          <FormKit
            type="form"
            name="partners-form"
            :actions="false"
            :disabled="isSubmiting"
            class="partners__form"
            form-class="md:w-[80%] w-full mx-auto"
            @submit="submitHandler"
          >
            <FormKit
              type="text"
              name="name"
              :placeholder="$t('contacts.form.name')"
              validation="required"
              input-class="bg-gray-light"
              outer-class="mb-[16px]"
            />
            <FormKit
              type="email"
              name="email"
              :placeholder="$t('contacts.form.email')"
              validation="required"
              input-class="bg-gray-light"
              outer-class="mb-[16px]"
            />
            <FormKit
              :type="baseSelect"
              name="select"
              :placeholder="$t('contacts.form.who-are-you')"
              :options="selectOptions"
              :option-name-key="'roleName'"
              input-class="h-full bg-gray-light"
              :is-contact-form="true"
              outer-class="mb-[16px]"
              validation="required"
            />

            <div class="relative">
              <FormKit
                v-model="message"
                type="textarea"
                name="description"
                :placeholder="$t('contacts.form.description')"
                input-class="h-full bg-gray-light"
                validation="required"
                rows="5"
                outer-class="mb-[16px]"
              />
              <div
                class="absolute right-0 top-[100%] font-medium"
                :class="{
                  'text-primary': message?.length <= 4096,
                  'text-error': message?.length > 4096,
                }"
              >
                {{ message?.length || 0 }}/4096
              </div>
            </div>
            <Checkbox v-model="isValidCapcha" class="mb-[20px] mt-8" />

            <FormKit
              v-model="isReaded"
              type="checkbox"
              name="isReaded"
              outer-class="$rest mb-[8px] formkit-disabled:opacity-50"
              label-class="mt-4 pb-[7px]"
              input-class="bg-primary border-primary checked:bg-primary"
              wrapper-class="md:items-center"
              inner-class="md:mt-[5px]"
            >
              <template #label>
                <div class="text-[16px] flex pt-[2px] md:items-center">
                  {{ $t("policy-window.contact-is-readed") }}&MediumSpace;
                  <div class="flex xl:flex-col md:flex-row flex-col">
                    <a
                      class="link underline hover:text-primary-hover"
                      href="https://kidscrafts.shop/privacy-policy/store-en.html"
                      target="_blank"
                    >
                      {{ $t("policy-window.privacy-policy") }},&MediumSpace;
                    </a>
                    <a
                      class="link underline hover:text-primary-hover"
                      href="https://kidscrafts.shop/agreement/store-en.html"
                      target="_blank"
                      >{{ $t("policy-window.user-agreement-and-terms") }}</a
                    >
                  </div>
                </div>
              </template>
            </FormKit>

            <button
              ref="button"
              class="btn--primary p-4 mt-8 w-[100%] partners__form-btn"
              :disabled="
                !isReaded ||
                isSubmiting ||
                !isValidCapcha ||
                message?.length > 4096
              "
            >
              {{ $t("common.send") }}
              <icon-send class="w-[24px] h-[24px]" />
            </button>
            <div v-show="response.isOpen" class="mt-4 font-medium">
              <div v-if="response.isSuccess" class="text-primary">
                {{ $t("contacts.success-modal.subtitle") }}
              </div>
              <div v-else class="text-error">
                {{ $t("contacts.error-modal.subtitle") }}
              </div>
            </div>
          </FormKit>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineEmits, defineProps, onMounted, ref, watch} from "vue";
import {createInput, reset} from "@formkit/vue";
import {Checkbox} from "vue-recaptcha/head";
import {createSupportRequest, getUserRoles} from "@/api/supportApi";

import BaseSelect from "@/components/elements/ui/BaseSelect.vue";
import PartnersFormHeader from "./PartnersFormHeader.vue";

const baseSelect = createInput(BaseSelect, {
  props: ["options", "optionNameKey", "isContactForm"],
});

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "partners",
  },
});

defineEmits({
  "close-modal": null,
});

const isSubmiting = ref(false);
const isValidCapcha = ref();
const selectOptions = ref([]);
const message = ref("");
const response = ref({
  isOpen: false,
  isSuccess: false,
});
const isReaded = ref(false);

const form_elem = ref();

const submitHandler = async (values, node) => {
  isSubmiting.value = true;
  if (!isValidCapcha.value) return;
  if (!isReaded.value) return;

  const clientRoleCode = values.select.code;
  const { description, email } = values;

  if (description?.length > 4096) return;
  const message = `Name: ${values.name}, message: ${description}`;
  try {
    await createSupportRequest({ clientRoleCode, description: message, email });
    response.value = { isOpen: true, isSuccess: true };
    reset(node);
  } catch (error) {
    response.value = { isOpen: true, isSuccess: false };
    throw error;
  }
  isSubmiting.value = false;
  isReaded.value = false;
};

onMounted(() => {
  getRoles();
});

watch(props, () => {
  if (!props.isOpen) return;
  setTimeout(() => {
    form_elem.value.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  });
});

const getRoles = async () => {
  try {
    selectOptions.value = await getUserRoles();
  } catch (error) {
    throw error;
  }
};
</script>

<style lang="scss" scoped>
.partners__form-field {
  margin-bottom: 1rem;
}

.partners__form {
  &-btn {
    padding-top: 1rem;
    padding-bottom: 1rem;
    border-radius: 15px;
  }

  &-field {
    margin-bottom: 1rem;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  overflow: hidden;
}

.modal-content {
  position: absolute;
  top: 60%;
  left: 50%;
  width: 95vw;
  min-width: 343px;
  z-index: 1000;
  transform: translate(-50%, -50%);
  scrollbar-width: none;
  -ms-overflow-style: none;

  @media (max-width: 768px) {
    transform: translateX(-50%);
    top: 2%;
  }

  @apply bg-light rounded-[20px];

  &__container {
    @apply md:pr-[42px] xl:pb-0 md:pb-[42px];

    @media (max-width: 768px) {
      @apply p-0;
    }

    ::-webkit-scrollbar {
      width: 4px;
    }

    ::-webkit-scrollbar-track {
      background: #d9d9d9;
    }

    ::-webkit-scrollbar-thumb {
      background: #908f8f;
      border-radius: 7px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: #555;
    }

    .police__header {
      h1 {
        font-size: 42px;
      }
    }
  }
}
</style>
